import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { database } from "../../firebaseConfig";
import { ref, get, set } from "firebase/database";
import CategorySelect from "./productComponents/CategorySelect";
import SearchBar from "../SearchBar";
import ProductsTable from "./productComponents/ProductsTable";
import Pagination from "./productComponents/Pagination";
import AddProductModal from "./productComponents/AddProductModal";
import "./ProductsPanel.css";

import { config } from '../../config/config'
const API_URL = config.baseUrl;

const ProductsPanel = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Modal state for adding new product
    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const [newProductData, setNewProductData] = useState({
        name: "",
        sku: "",
        description: "",
        related_products: "",
        additional_info: ""
    });
    const [mainImageFile, setMainImageFile] = useState(null);
    const [detailImagesFiles, setDetailImagesFiles] = useState([]);

    // Refs for file inputs in the products table
    const fileInputRefs = useRef([]);
    const mainImageRefs = useRef([]);

    // Fetch categories on mount
    useEffect(() => {
        fetchCategories();
    }, []);

    // Fetch products when selected category changes; reset page to 1
    useEffect(() => {
        if (selectedCategoryIndex !== null && selectedCategoryIndex >= 0) {
            fetchProducts();
        } else {
            setProducts([]);
        }
        setCurrentPage(1);
    }, [selectedCategoryIndex]);

    const fetchCategories = async () => {
        try {
            const snapshot = await get(ref(database, "categories"));
            if (snapshot.exists()) {
                const data = snapshot.val();
                const cats = Array.isArray(data) ? data : [];
                setCategories(cats);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const fetchProducts = async () => {
        if (selectedCategoryIndex == null) return;
        try {
            const snapshot = await get(
                ref(database, `categories/${selectedCategoryIndex}/products`)
            );
            if (snapshot.exists()) {
                setProducts(snapshot.val());
            } else {
                setProducts([]);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        }
    };

    // Helper: extract filename from URL
    const extractFilename = (url) => {
        try {
            const parts = url.split("/");
            return parts[parts.length - 1];
        } catch (error) {
            console.error("Error extracting filename:", error);
            return "";
        }
    };

    const deleteFileFromServer = async (fileUrl) => {
        if (!fileUrl) return;
        const filename = extractFilename(fileUrl);
        try {
            await axios.delete(`${API_URL}delete`, { data: { filename } });
        } catch (error) {
            console.error("Error deleting file from server:", error);
        }
    };

    const handleFileUpload = async (productIndex, file, isMainImage = false) => {
        if (!file) return;
        const formData = new FormData();
        formData.append("media", file);
        try {
            const response = await axios.post(`${API_URL}upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            if (response.data.fileUrl) {
                const imageUrl = response.data.fileUrl;
                const updated = [...products];
                if (isMainImage) {
                    updated[productIndex].image = imageUrl;
                } else {
                    updated[productIndex].detail_images =
                        updated[productIndex].detail_images || [];
                    updated[productIndex].detail_images.push(imageUrl);
                }
                setProducts(updated);
                // Update DB
                await set(
                    ref(
                        database,
                        `categories/${selectedCategoryIndex}/products/${productIndex}`
                    ),
                    updated[productIndex]
                );
            }
        } catch (error) {
            console.error("Error uploading file:", error);
        }
    };

    const handleRemoveImage = async (productIndex, imageIndex = null, isMainImage = false) => {
        const updated = [...products];
        let fileUrl = "";
        if (isMainImage) {
            fileUrl = updated[productIndex].image;
            updated[productIndex].image = "";
        } else {
            fileUrl = updated[productIndex].detail_images[imageIndex];
            updated[productIndex].detail_images.splice(imageIndex, 1);
        }
        await deleteFileFromServer(fileUrl);
        setProducts(updated);
        await set(
            ref(database, `categories/${selectedCategoryIndex}/products/${productIndex}`),
            updated[productIndex]
        );
    };

    const saveProductField = async (index, field, value) => {
        const updated = [...products];
        updated[index][field] = value;
        setProducts(updated);
        await set(
            ref(database, `categories/${selectedCategoryIndex}/products/${index}`),
            updated[index]
        );
    };

    const deleteRow = async (index) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        const updated = products.filter((_, i) => i !== index);
        setProducts(updated);
        await set(
            ref(database, `categories/${selectedCategoryIndex}/products`),
            updated
        );
    };

    const handleCategorySelect = (e) => {
        const idx = e.target.value;
        if (idx !== "") {
            setSelectedCategoryIndex(Number(idx));
            setSelectedCategory(categories[Number(idx)]);
        } else {
            setSelectedCategoryIndex(null);
            setSelectedCategory(null);
            setProducts([]);
        }
    };

    // Filter and paginate products
    const filteredProducts = products.filter((p) => {
        const term = searchTerm.toLowerCase();
        return (
            (p.name && p.name.toLowerCase().includes(term)) ||
            (p.sku && p.sku.toLowerCase().includes(term))
        );
    });

    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const displayedProducts = filteredProducts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // --- Modal Logic for Adding New Product ---
    const openAddProductModal = () => {
        setNewProductData({
            name: "",
            sku: "",
            description: "",
            related_products: "",
            additional_info: ""
        });
        setMainImageFile(null);
        setDetailImagesFiles([]);
        setShowAddProductModal(true);
    };

    const closeAddProductModal = () => {
        setShowAddProductModal(false);
    };

    const handleNewProductChange = (field, value) => {
        setNewProductData((prev) => ({ ...prev, [field]: value }));
    };

    const handleMainImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setMainImageFile(e.target.files[0]);
        }
    };

    const handleDetailImagesChange = (e) => {
        if (e.target.files) {
            setDetailImagesFiles(Array.from(e.target.files));
        }
    };

    const uploadFile = async (file) => {
        if (!file) return "";
        const formData = new FormData();
        formData.append("media", file);
        try {
            const response = await axios.post(`${API_URL}upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            return response.data.fileUrl || "";
        } catch (error) {
            console.error("Error uploading file:", error);
            return "";
        }
    };

    const saveNewProduct = async () => {
        const { name, sku, description, related_products } = newProductData;
        if (
            !name ||
            !sku ||
            !mainImageFile
        ) {
            alert("Please fill in all required fields (all except Additional Info).");
            return;
        }
        const mainImageUrl = await uploadFile(mainImageFile);
        if (!mainImageUrl) {
            alert("Error uploading main image.");
            return;
        }
        const detailImagesUrls = [];
        for (let file of detailImagesFiles) {
            const url = await uploadFile(file);
            if (url) {
                detailImagesUrls.push(url);
            }
        }
        if (detailImagesUrls.length === 0) {
            alert("Error uploading detail images.");
            return;
        }
        const newProduct = {
            ...newProductData,
            image: mainImageUrl,
            detail_images: detailImagesUrls
        };
        const updated = [...products, newProduct];
        setProducts(updated);
        await set(
            ref(database, `categories/${selectedCategoryIndex}/products`),
            updated
        );
        closeAddProductModal();
    };

    return (
        <div className="products-panel">
            <h2>Products Panel</h2>
            <CategorySelect
                categories={categories}
                selectedCategoryIndex={selectedCategoryIndex}
                handleCategorySelect={handleCategorySelect}
            />
            {selectedCategory && (
                <SearchBar
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    setCurrentPage={setCurrentPage}
                    placeholder="Search by name or SKU..."
                />
            )}
            {selectedCategory ? (
                <>
                    <h3>Products in {selectedCategory.category}</h3>
                    <button className="add-product-btn" onClick={openAddProductModal}>
                        ADD NEW PRODUCT
                    </button>
                    <ProductsTable
                        displayedProducts={displayedProducts}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        handleFileUpload={handleFileUpload}
                        handleRemoveImage={handleRemoveImage}
                        saveProductField={saveProductField}
                        deleteRow={deleteRow}
                        fileInputRefs={fileInputRefs}
                        mainImageRefs={mainImageRefs}
                    />
                    <Pagination
                        totalPages={totalPages}
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                    />
                </>
            ) : (
                <div>Please select a category to view products.</div>
            )}
            <AddProductModal
                show={showAddProductModal}
                newProductData={newProductData}
                handleNewProductChange={handleNewProductChange}
                handleMainImageChange={handleMainImageChange}
                handleDetailImagesChange={handleDetailImagesChange}
                saveNewProduct={saveNewProduct}
                closeAddProductModal={closeAddProductModal}
            />
        </div>
    );
};

export default ProductsPanel;
